import { Component, ForwardedRef, ReactNode, forwardRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { isEmpty } from 'lodash/fp';
import { KeyValue } from '@mews/enums';
import { TooltippedIcon, CommanderFormContext } from '@mews-commander/ui';
import { IconName } from '@optimus-web/icons';
import { SearchType } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkAction, CommanderThunkDispatch, SearchResult } from '@mews-commander/types';
import { SearchInput, TextInputProps } from '@optimus-web/core';
import { FeatureFlagServiceProps, getNormalizedPathname, withFeatureFlagService } from '@mews/framework';
import { isFetching as isFetchingSelector } from '../selectors';
import { search, searchMore, SearchMoreParams, SearchParams } from '../actions';
import { isValidSearchTerm } from '../utils';
import ResultDrawer from './ResultDrawer';
import { SearchResultSuffix } from './SearchResultSuffix';
import { ResultDrawerContainer } from './style';

export interface SearchProps extends TextInputProps {
    inputName?: string;
    onHideClick?: () => void;
    onResultClick?: (result: SearchResult) => void;
    onResultClear?: () => void;
    scope: string;
    searchCount?: number;
    searchTypes: SearchType[];
    selectedResult?: SearchResult | null;
    autoFocus?: boolean;
    enterpriseId?: string;
    optionalSearchParams?: {};
    loaderVariant?: string;
    showSectionTitles?: boolean;
    except?: string[];
    newEntity?: boolean;
    'data-test-id'?: string;
    preserveSearchTermOnSelect?: boolean;
    showInvoiceableTag?: boolean;
    variant?: 'contrast';
    bottomCaption?: ReactNode;
    forwardedRef?: ForwardedRef<HTMLInputElement>;
}

interface StateProps {
    isFetching?: boolean;
}
interface DispatchProps {
    onSearch: (searchParams: SearchParams) => void;
    onSearchMore: (searchParams: SearchMoreParams) => void;
}

interface State {
    moreClicked: boolean;
    searchFocused: boolean;
    searchTerm: string;
    isSearchTermDirty: boolean;
    isInputFocused: boolean;
}

type Props = SearchProps & StateProps & DispatchProps & FeatureFlagServiceProps;
class BaseSearch extends Component<Props, State> {
    static defaultProps = {
        searchCount: 10,
    };

    declare context: React.ContextType<typeof CommanderFormContext>;

    resultDrawer: $TSFixMe;
    resultDrawerComponent: $TSFixMe;
    searchElement: $TSFixMe;

    state = {
        moreClicked: false,
        searchFocused: false,
        searchTerm: '',
        isSearchTermDirty: false,
        isInputFocused: false,
    };

    handleSearchMore = (searchType: SearchType) => {
        // This is required because the button changes into loader and ClickAway can't find it anymore
        this.setState({ moreClicked: true });
        this.props.onSearchMore({
            searchType,
            searchTerm: this.state.searchTerm,
            count: this.props.searchCount as number,
            enterpriseId: this.props.enterpriseId,
            optionalParams: this.props.optionalSearchParams,
        });
    };

    handleInputKeyDown = (e: $TSFixMe) => {
        let result;
        let consumed;

        switch (e.key) {
            case KeyValue.ArrowUp:
                consumed = true;
                this.showResults();
                this.resultDrawer.navigateUp();
                break;
            case KeyValue.ArrowDown:
                consumed = true;
                this.showResults();
                this.resultDrawer.navigateDown();
                break;
            case KeyValue.Enter:
                if (this.resultDrawer) {
                    result = this.resultDrawer.getSelectedResult();
                }

                if (result) {
                    this.handleResultClick(result);
                    consumed = true;
                }
                break;
            case KeyValue.Escape:
                consumed = true;
                this.hideResults();
                break;
            default:
                break;
        }

        if (consumed) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    handleInputFocus = () => {
        this.setState({ isInputFocused: true });
        this.showResults();
    };

    handleInputBlur = () => {
        this.setState({ isInputFocused: false });
    };

    handleClickAway = () => {
        if (this.state.searchFocused) {
            if (this.state.moreClicked) {
                this.setState({ moreClicked: false });
                return;
            }

            this.hideResults();
        }
    };

    handleClearClick = () => {
        if (this.props.selectedResult) {
            this.props.onResultClear?.();
        } else {
            this.setState({ searchTerm: '', isSearchTermDirty: false });
        }
    };

    handleIconClick = () => {
        // @ts-expect-error $TSFixMeError: Property 'interactiveIconHandler' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
        this.props.interactiveIconHandler();
        this.hideResults();
    };

    showResults = () => {
        if (!this.state.searchFocused) {
            this.setState({ searchFocused: true });
        }
    };

    hideResults = () => {
        if (this.state.searchFocused) {
            this.setState({ searchFocused: false });

            if (this.resultDrawer) {
                this.resultDrawer.clearSelectedResult();
            }
        }
    };

    handleResultClick = (result: SearchResult) => {
        this.hideResults();
        if (!this.props.preserveSearchTermOnSelect) {
            this.setState({ searchTerm: '' });
        }
        this.props.onResultClick?.(result);
    };

    resultDrawerRef = (c: $TSFixMe) => (this.resultDrawer = c);

    searchElementRef = (c: $TSFixMe) => {
        this.searchElement = c;
        // @ts-expect-error $TSFixMeError: Property 'ref' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
        if (this.props.ref) {
            // @ts-expect-error $TSFixMeError: Property 'ref' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            this.props.ref(c);
        }
    };

    handleInputChange = (e: $TSFixMe, value: $TSFixMe) => {
        this.setState({ searchTerm: value, isSearchTermDirty: true });
    };

    handleSearch = () => {
        const { isSearchTermDirty, searchTerm } = this.state;
        const { searchTypes, optionalSearchParams } = this.props;
        if (isSearchTermDirty && isValidSearchTerm(searchTerm)) {
            this.props.onSearch({
                searchTypes,
                searchTerm,
                count: this.props.searchCount as number,
                enterpriseId: this.props.enterpriseId,
                optionalParams: optionalSearchParams,
            });
            this.setState({ isSearchTermDirty: false });
        }
    };

    render() {
        const { searchFocused, searchTerm, isSearchTermDirty, isInputFocused } = this.state;
        const {
            placeholder,
            error,
            selectedResult,
            inputName,
            isFetching,
            'data-test-id': dataTestId,
            disabled,
            size,
            label,
            required,
            variant,
            inline,
            searchTypes,
            scope,
            except,
            newEntity,
            showSectionTitles,
            helper,
            showInvoiceableTag,
            bottomCaption,
            name,
        } = this.props;

        const isValidTerm = isValidSearchTerm(searchTerm);
        const showLoader = (isValidTerm && isSearchTermDirty) || isFetching;
        const showResults = searchFocused && !showLoader && !disabled && (isValidTerm || selectedResult);

        let selectedLabel;
        let selectedValue = '';
        if (selectedResult) {
            const { id, title, description } = selectedResult;

            selectedValue = id;
            selectedLabel = title;
            if (!isEmpty(description)) {
                selectedLabel = `${title} - ${description}`;
            }
        }

        const drawerProps = {
            ref: this.resultDrawerRef,
            anchorElement: this.searchElement,
            hidden: !showResults,
            showSectionTitle: showSectionTitles ?? false,
            onResultClick: this.handleResultClick,
            onMoreClick: this.handleSearchMore,
            searchProps: { searchTerm, searchTypes, scope, except, newEntity },
            showInvoiceableTag: showInvoiceableTag ?? false,
            handleClickAway: this.handleClickAway,
        };

        const captionIconWithTooltip = helper ? (
            <TooltippedIcon size="small" value={IconName.Info} tooltip={helper} />
        ) : null;

        const { readOnlyForm } = this.context;

        const searchFieldProps = {
            error,
            placeholder,
            'data-test-id': dataTestId,
            'data-test-search-field-name': inputName,
            onFocus: this.handleInputFocus,
            onBlur: this.handleInputBlur,
            onKeyDown: this.handleInputKeyDown,
            onClear: this.handleClearClick,
            onSearch: this.handleSearch,
            onChange: this.handleInputChange,
            value: searchTerm,
            loading: !!showLoader,
            disabled: readOnlyForm || disabled,
            selectedLabel,
            isInputFocused,
            size,
            label,
            required,
            variant,
            inline,
            captionIconWithTooltip,
            helper: bottomCaption,
            name,
            suffix: <SearchResultSuffix searchResult={selectedResult} />,
            interactiveIcon: undefined,
            interactiveIconHandler: undefined,
        };

        return (
            <div ref={this.searchElementRef}>
                <SearchInput {...searchFieldProps} ref={this.props.forwardedRef} />
                <ResultDrawerContainer>
                    <ResultDrawer {...drawerProps} />
                </ResultDrawerContainer>
                {inputName && <input type="hidden" name={inputName} value={selectedValue} />}
            </div>
        );
    }
}

BaseSearch.contextType = CommanderFormContext;

const Search = forwardRef<HTMLInputElement, Props>((props, ref) => <BaseSearch {...props} forwardedRef={ref} />);

const logSearchMoreUsage =
    (searchType: SearchType): CommanderThunkAction =>
    (dispatch, getState, { loggerService }) => {
        loggerService.log('search-more-click', 'Search more button was clicked on', {
            url: getNormalizedPathname(window),
            searchType,
        });
    };

export default connect<StateProps, DispatchProps, SearchProps, ApplicationState>(
    createStructuredSelector({
        isFetching: isFetchingSelector,
    }),
    (dispatch: CommanderThunkDispatch, { scope }) => ({
        onSearch: async (searchParams) => dispatch(search(searchParams, scope)),
        onSearchMore: (searchParams) => {
            const { searchType } = searchParams;

            dispatch(logSearchMoreUsage(searchType));
            return dispatch(searchMore(searchParams, scope));
        },
    }),
    undefined,
    { forwardRef: true }
)(withFeatureFlagService(Search));
